// src/services/salonDetailsService.js
// Service pour les détails d'un salon - VERSION PRODUCTION

import api from './api';

/**
 * 🛠️ HELPER - Normaliser les URLs d'images
 * MÊME LOGIQUE que salonService.js
 */
const normalizeImageUrl = (imageUrl) => {
  if (!imageUrl) return null;

  // URL absolue déjà valide (CDN / API) — ne pas la réécrire
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  const apiUrl = process.env.REACT_APP_API_URL || 'https://api.shynea.one/api';
  const baseUrl = apiUrl.replace(/\/api\/?$/, '');

  if (imageUrl.startsWith('/uploads/')) {
    return `${baseUrl}${imageUrl}`;
  }

  if (imageUrl.startsWith('/')) {
    return `${baseUrl}${imageUrl}`;
  }

  // Chemins legacy sans préfixe → services par défaut
  return `${baseUrl}/uploads/services/${imageUrl}`;
};

const salonDetailsService = {
  
  /**
   * 🎯 Récupérer toutes les données d'un salon
   */
  async getAllSalonData(salonId) {
    try {
      const [salon, services, reviews, portfolio] = await Promise.all([
        this.getSalonDetails(salonId),
        this.getSalonServices(salonId),
        this.getSalonReviews(salonId),
        this.getSalonPortfolio(salonId)
      ]);
      
      return { salon, services, reviews, portfolio };
    } catch (error) {
      console.error('❌ Erreur getAllSalonData:', error);
      throw error;
    }
  },

  /**
   * 🏢 Détails du salon
   */
  async getSalonDetails(salonId) {
    try {
      const response = await api.get(`/public/salons/${salonId}`);
      const salon = response.data.salon || response.data;

      return {
        ...salon,
        cover_image:       normalizeImageUrl(salon.cover_image || salon.image_url),
        image_url:         normalizeImageUrl(salon.image_url || salon.cover_image),
        // Champs nécessaires pour la logique de réservation
        service_type:      salon.service_type      || null,
        transport_fee_min: salon.transport_fee_min  || null,
        wave_phone:        salon.wave_phone         || null,
        orange_phone:      salon.orange_phone       || null,
      };
    } catch (error) {
      console.error('❌ Erreur getSalonDetails:', error);
      throw error;
    }
  },

  /**
   * ✂️ Services du salon
   */
  async getSalonServices(salonId) {
    try {
      const response = await api.get(`/public/salons/${salonId}/services`);
      
      const services = response.data.services || response.data || [];
      
      return services.map(service => ({
        ...service,
        image: normalizeImageUrl(service.image_url)
      }));
    } catch (error) {
      console.error('❌ Erreur getSalonServices:', error);
      return [];
    }
  },

  /**
   * 🔍 Filtrer les services par catégorie
   */
  filterServicesByCategory(services, category) {
    if (!Array.isArray(services)) return [];
    if (category === 'all') return services;
    return services.filter(s => s.category_name?.toLowerCase() === category.toLowerCase());
  },

  /**
   * 📊 Extraire les catégories uniques des services
   * @param {Array} services - Liste des services
   * @returns {Array} Liste des catégories uniques triées alphabétiquement
   */
  getUniqueCategories(services) {
    if (!Array.isArray(services) || services.length === 0) return [];
    
    // Extraire les catégories uniques (en minuscules)
    const categories = [...new Set(
      services
        .map(s => s.category_name)
        .filter(Boolean) // Enlever les null/undefined
    )];
    
    // Trier par ordre alphabétique
    return categories.sort((a, b) => a.localeCompare(b, 'fr'));
  },

  /**
   * 🔤 Capitaliser la première lettre d'un mot
   */
  capitalizeFirstLetter(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  },

  /**
   * ⭐ Avis du salon
   */
  async getSalonReviews(salonId, limit = null) {
    try {
      const url = limit 
        ? `/reviews/salon/${salonId}?limit=${limit}` 
        : `/reviews/salon/${salonId}`;
      
      const response = await api.get(url);
      
      return response.data.reviews || response.data || [];
    } catch (error) {
      console.error('❌ Erreur getSalonReviews:', error);
      return [];
    }
  },

  /**
   * 🖼️ Portfolio du salon
   */
  async getSalonPortfolio(salonId, limit = null) {
    try {
      const url = limit 
        ? `/portfolio/salon/${salonId}?limit=${limit}` 
        : `/portfolio/salon/${salonId}`;
      
      const response = await api.get(url);
      
      const items = response.data.items || [];
      
      return items.map(item => ({
        ...item,
        image_url: normalizeImageUrl(item.media_url),
        thumbnail_url: normalizeImageUrl(item.thumbnail_url)
      }));
    } catch (error) {
      console.error('❌ Erreur getSalonPortfolio:', error);
      return [];
    }
  },

  /**
   * 🛠️ UTILITAIRES
   */
  parseOpeningHours(hoursString) {
    try {
      if (!hoursString) return {};
      if (typeof hoursString === 'object') return hoursString;
      return JSON.parse(hoursString);
    } catch {
      return {};
    }
  },

  formatRelativeDate(dateString) {
    if (!dateString) return 'Date inconnue';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaine${Math.floor(diffDays / 7) > 1 ? 's' : ''}`;
    return `Il y a ${Math.floor(diffDays / 30)} mois`;
  },

  renderStars(rating) {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    return (
      '★'.repeat(fullStars) +
      (hasHalfStar ? '☆' : '') +
      '☆'.repeat(5 - fullStars - (hasHalfStar ? 1 : 0))
    );
  }
};

export default salonDetailsService;
